import type { AdminInfo, DomainMember, ProjectMember, WorkspaceMember } from '@protoss/shared';
import { Button, Select, SelectItem } from '@protoss/ui';
import * as React from 'react';
import { apiFetch } from '../../lib/apiClient';

/**
 * DomainAdminInfo - read-only display of the current domain administrator(s)
 *
 * Receives pre-fetched admins from the parent. `undefined` means still loading.
 */
export function DomainAdminInfo({ admins }: { admins: AdminInfo[] | undefined }) {
  if (admins === undefined) return <p className="entity-info__empty">Loading…</p>;
  if (admins.length === 0) return <p className="entity-info__empty">No administrators assigned.</p>;

  return (
    <ul className="entity-info__admin-list">
      {admins.map((a) => (
        <li key={a.uuid} className="entity-info__admin-item">
          <div className="entity-info__admin-item-info">
            <span className="entity-info__admin-name">{a.displayName ?? a.email}</span>
            {a.displayName && <span className="entity-info__admin-email">{a.email}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * ProjectAdminInfo - read-only display of the current project administrator(s)
 *
 * Receives pre-fetched admins from the parent. `undefined` means still loading.
 */
export function ProjectAdminInfo({ admins }: { admins: AdminInfo[] | undefined }) {
  if (admins === undefined) return <p className="entity-info__empty">Loading…</p>;
  if (admins.length === 0) return <p className="entity-info__empty">No administrators assigned.</p>;

  return (
    <ul className="entity-info__admin-list">
      {admins.map((a) => (
        <li key={a.uuid} className="entity-info__admin-item">
          <div className="entity-info__admin-item-info">
            <span className="entity-info__admin-name">{a.displayName ?? a.email}</span>
            {a.displayName && <span className="entity-info__admin-email">{a.email}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}

interface DomainMembersPanelProps {
  domainUuid: string;
  workspaceMembers: WorkspaceMember[];
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
  collaborator: 'Collaborator',
};

const ROLE_OPTIONS = ['admin', 'manager', 'viewer', 'collaborator'] as const;

/**
 * DomainMembersPanel - displays and manages domain-level role assignments
 *
 * Shows the current list of domain members with their roles and provides
 * an "Add member" form to assign a workspace member to a domain role.
 *
 * This panel is self-contained: it fetches its own member list and
 * handles submissions independently of the parent DomainForm.
 *
 * Used in the domain edit page below the name/description form.
 */
export function DomainMembersPanel({ domainUuid, workspaceMembers }: DomainMembersPanelProps) {
  const [members, setMembers] = React.useState<DomainMember[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [addUserUuid, setAddUserUuid] = React.useState('');
  const [addRole, setAddRole] = React.useState<string>('viewer');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  const fetchMembers = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiFetch<{ members: DomainMember[] }>(`/api/domain/${domainUuid}/members`);
      setMembers(data.members ?? []);
    } catch {
      // Non-critical: leave list empty
    } finally {
      setIsLoading(false);
    }
  }, [domainUuid]);

  React.useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  // Filter out workspace members who already have an active domain role
  const existingMemberUuids = new Set(members.map((m) => m.uuid));
  const availableToAdd = workspaceMembers.filter((wm) => !existingMemberUuids.has(wm.uuid));

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addUserUuid) return;

    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      await apiFetch(`/api/domain/${domainUuid}/member`, {
        method: 'POST',
        body: JSON.stringify({ userUuid: addUserUuid, role: addRole }),
      });
      setAddUserUuid('');
      setAddRole('viewer');
      setSubmitSuccess(true);
      await fetchMembers();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to assign role');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="members-panel">
      <h2 className="members-panel__title">Domain Members</h2>

      {isLoading ? (
        <p className="members-panel__loading">Loading members…</p>
      ) : members.length === 0 ? (
        <p className="members-panel__empty">
          No members assigned yet. Add the first domain administrator below.
        </p>
      ) : (
        <ul className="members-panel__list">
          {members.map((member) => (
            <li key={member.uuid} className="members-panel__member">
              <div className="members-panel__member-info">
                <span className="members-panel__member-name">
                  {member.displayName ?? member.email}
                </span>
                {member.displayName && (
                  <span className="members-panel__member-email">{member.email}</span>
                )}
              </div>
              <span className="members-panel__member-role">
                {ROLE_LABELS[member.role] ?? member.role}
              </span>
            </li>
          ))}
        </ul>
      )}

      <form className="members-panel__add-form" onSubmit={handleAddMember}>
        <h3 className="members-panel__add-title">Add member</h3>

        <div className="members-panel__add-fields">
          <div className="members-panel__add-field">
            <label className="members-panel__label">Workspace member</label>
            <Select
              value={addUserUuid}
              onValueChange={setAddUserUuid}
              placeholder="Select a workspace member"
              disabled={isSubmitting || availableToAdd.length === 0}
            >
              {availableToAdd.map((member) => (
                <SelectItem key={member.uuid} value={member.uuid}>
                  {member.displayName ?? member.email}
                  {member.isAdmin ? ' (workspace admin)' : ''}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className="members-panel__add-field">
            <label className="members-panel__label">Role</label>
            <Select value={addRole} onValueChange={setAddRole} disabled={isSubmitting}>
              {ROLE_OPTIONS.map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </Select>
          </div>
        </div>

        {submitError && (
          <p className="members-panel__error" role="alert">
            {submitError}
          </p>
        )}
        {submitSuccess && (
          <p className="members-panel__success" role="status">
            Member added successfully.
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting || !addUserUuid || availableToAdd.length === 0}
        >
          {isSubmitting ? 'Adding…' : 'Add member'}
        </Button>
      </form>
    </div>
  );
}

interface ProjectMembersPanelProps {
  projectUuid: string;
  workspaceMembers: WorkspaceMember[];
}

/**
 * ProjectMembersPanel - displays and manages project-level role assignments
 *
 * Shows the current list of project members with their roles and provides
 * an "Add member" form to assign a workspace member to a project role.
 *
 * This panel is self-contained: it fetches its own member list and
 * handles submissions independently of the parent ProjectForm.
 *
 * Used in the project edit page below the name/description form.
 */
export function ProjectMembersPanel({ projectUuid, workspaceMembers }: ProjectMembersPanelProps) {
  const [members, setMembers] = React.useState<ProjectMember[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [addUserUuid, setAddUserUuid] = React.useState('');
  const [addRole, setAddRole] = React.useState<string>('viewer');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  const fetchMembers = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiFetch<{ members: ProjectMember[] }>(
        `/api/project/${projectUuid}/members`
      );
      setMembers(data.members ?? []);
    } catch {
      // Non-critical: leave list empty
    } finally {
      setIsLoading(false);
    }
  }, [projectUuid]);

  React.useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  // Filter out workspace members who already have an active project role
  const existingMemberUuids = new Set(members.map((m) => m.uuid));
  const availableToAdd = workspaceMembers.filter((wm) => !existingMemberUuids.has(wm.uuid));

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addUserUuid) return;

    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      await apiFetch(`/api/project/${projectUuid}/member`, {
        method: 'POST',
        body: JSON.stringify({ userUuid: addUserUuid, role: addRole }),
      });
      setAddUserUuid('');
      setAddRole('viewer');
      setSubmitSuccess(true);
      await fetchMembers();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to assign role');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="members-panel">
      <h2 className="members-panel__title">Project Members</h2>

      {isLoading ? (
        <p className="members-panel__loading">Loading members…</p>
      ) : members.length === 0 ? (
        <p className="members-panel__empty">
          No members assigned yet. Add the first project member below.
        </p>
      ) : (
        <ul className="members-panel__list">
          {members.map((member) => (
            <li key={member.uuid} className="members-panel__member">
              <div className="members-panel__member-info">
                <span className="members-panel__member-name">
                  {member.displayName ?? member.email}
                </span>
                {member.displayName && (
                  <span className="members-panel__member-email">{member.email}</span>
                )}
              </div>
              <span className="members-panel__member-role">
                {ROLE_LABELS[member.role] ?? member.role}
              </span>
            </li>
          ))}
        </ul>
      )}

      <form className="members-panel__add-form" onSubmit={handleAddMember}>
        <h3 className="members-panel__add-title">Add member</h3>

        <div className="members-panel__add-fields">
          <div className="members-panel__add-field">
            <label className="members-panel__label">Workspace member</label>
            <Select
              value={addUserUuid}
              onValueChange={setAddUserUuid}
              placeholder="Select a workspace member"
              disabled={isSubmitting || availableToAdd.length === 0}
            >
              {availableToAdd.map((member) => (
                <SelectItem key={member.uuid} value={member.uuid}>
                  {member.displayName ?? member.email}
                  {member.isAdmin ? ' (workspace admin)' : ''}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className="members-panel__add-field">
            <label className="members-panel__label">Role</label>
            <Select value={addRole} onValueChange={setAddRole} disabled={isSubmitting}>
              {ROLE_OPTIONS.map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </Select>
          </div>
        </div>

        {submitError && (
          <p className="members-panel__error" role="alert">
            {submitError}
          </p>
        )}
        {submitSuccess && (
          <p className="members-panel__success" role="status">
            Member added successfully.
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting || !addUserUuid || availableToAdd.length === 0}
        >
          {isSubmitting ? 'Adding…' : 'Add member'}
        </Button>
      </form>
    </div>
  );
}
